import { useEffect, useState } from 'react';
import { apiClient, type SourceSummary, type SourceCreated, ApiError } from '@/lib/api/client';
import { NewSourceModal } from './NewSourceModal';
import { SourceConfigDrawer } from './SourceConfigDrawer';
import { RulesView } from '@/features/rules/RulesView';
import { cn } from '@/lib/utils';
import { useDialogFocus } from '@/lib/useDialogFocus';

interface Props {
  onBack: () => void;
  workspaceName: string;
}

/**
 * SourcesView — list + create + configure + delete. Renders RulesView as a
 * full replacement when openRulesSource is set (no overlay).
 */
export function SourcesView({ onBack, workspaceName }: Props) {
  const [list, setList] = useState<SourceSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState<SourceCreated | null>(null);
  const [openRulesSource, setOpenRulesSource] = useState<SourceSummary | null>(null);
  const [configSourceId, setConfigSourceId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  async function refresh() {
    try {
      const r = await apiClient.listSources();
      setList(r.sources);
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to load');
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  // IMPORTANT: any conditional return MUST be after ALL hooks above.
  // (React rules of hooks — conditional hook execution causes error #300.)
  if (openRulesSource) {
    return (
      <RulesView
        sourceId={openRulesSource.source_id}
        sourceName={openRulesSource.name}
        onBack={() => setOpenRulesSource(null)}
      />
    );
  }

  /**
   * Two-step inline confirmation (audit fix #5): first call sets pendingDelete +
   * auto-clears in 3s; second call within the window performs the delete.
   * No native confirm()/alert() — replaced for keyboard-trap compliance (WCAG 4.1.3).
   */
  async function destroy(source_id: string) {
    if (pendingDelete !== source_id) {
      setPendingDelete(source_id);
      setTimeout(() => {
        setPendingDelete((curr) => (curr === source_id ? null : curr));
      }, 3000);
      return;
    }
    setPendingDelete(null);
    try {
      await apiClient.deleteSource(source_id);
      refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to delete');
    }
  }

  return (
    <main className="min-h-dvh motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-150 motion-safe:ease-out bg-background text-foreground antialiased">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-card focus:px-4 focus:py-2 focus:text-sm">Skip to content</a>
      <header className="border-b border-border bg-card/50">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors duration-150 hover:border-ring/60 hover:text-foreground"
            >
              ← Workspace
            </button>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Sources · {workspaceName}
            </div>
          </div>
          <button
            onClick={() => setCreating(true)}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 active:scale-[0.98]"
          >
            New source
          </button>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="space-y-1">
          <h1 className="text-balance text-3xl font-semibold tracking-tight md:text-4xl">
            Sources
          </h1>
          <p className="font-mono text-xs text-muted-foreground">
            External systems that send events into this workspace's inbox.
          </p>
        </div>
        {err && (
          <div role="alert" className="mt-6 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
            {err}
          </div>
        )}
        <div className="mt-8 space-y-3">
          {list === null ? (
            <SourcesSkeleton />
          ) : list.length === 0 ? (
            <Empty onCreate={() => setCreating(true)} />
          ) : (
            list.map((s) => (
              <SourceRow
                key={s.source_id}
                source={s}
                pendingDelete={pendingDelete === s.source_id}
                onDelete={() => destroy(s.source_id)}
                onOpenRules={() => setOpenRulesSource(s)}
                onConfigure={() => setConfigSourceId(s.source_id)}
              />
            ))
          )}
        </div>
      </div>
      {creating && (
        <NewSourceModal
          onCreated={(created) => {
            setCreating(false);
            setJustCreated(created);
            refresh();
          }}
          onClose={() => setCreating(false)}
        />
      )}
      {justCreated && (
        <SecretsRevealModal
          source={justCreated}
          onClose={() => setJustCreated(null)}
        />
      )}
    </main>
  );
}

function Empty({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-card/40 px-5 py-12 text-center">
      <div className="text-base text-foreground">No sources yet</div>
      <div className="mt-1 text-sm text-muted-foreground">
        Connect any external system that should pipe events into this inbox.
      </div>
      <button
        onClick={onCreate}
        className="mt-4 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 active:scale-[0.98]"
      >
        Add your first source
      </button>
    </div>
  );
}

function SourcesSkeleton() {
  return (
    <div className="space-y-3" aria-busy="true" aria-label="Loading sources">
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="h-20 animate-pulse rounded-xl bg-card" />
      ))}
    </div>
  );
}

function SourceRow({
  source,
  pendingDelete,
  onDelete,
  onOpenRules,
  onConfigure,
}: {
  source: SourceSummary;
  pendingDelete: boolean;
  onDelete: () => void;
  onOpenRules: () => void;
  onConfigure: () => void;
}) {
  const health = source.last_event_at
    ? Date.now() - source.last_event_at < 7 * 24 * 60 * 60 * 1000
      ? { label: 'Active', tone: 'oklch(0.72 0.18 148)' as const }
      : { label: 'Stale', tone: 'oklch(0.78 0.17 65)' as const }
    : { label: 'No events', tone: 'oklch(0.78 0.17 65)' as const };
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <div
              className="size-3 rounded-full"
              style={{ background: source.color ?? 'var(--primary)' }}
              aria-hidden
            />
            <div className="truncate font-semibold text-foreground">{source.name}</div>
            <code className="font-mono text-[10px] text-muted-foreground">/{source.slug}</code>
            {/* Health: leading dot + muted-mono text. Tinted pill broke
                "one alarm, not five" — green-when-fine trained users to
                ignore the column. Now only the dot color carries state. */}
            <span
              className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground"
              aria-label={`Source health: ${health.label}`}
            >
              <span
                aria-hidden
                className="size-1.5 rounded-full"
                style={{ background: health.tone }}
              />
              {health.label}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-2 text-xs md:grid-cols-2">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                API key
              </div>
              <code className="font-mono text-[11px] text-foreground">
                {source.sk_live_prefix}…
              </code>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Signing secret
              </div>
              <code className="font-mono text-[11px] text-foreground">
                {source.whsec_prefix}…
              </code>
            </div>
          </div>
          <div className="mt-2 font-mono text-[10px] text-muted-foreground">
            {source.last_event_at
              ? `Last event: ${new Date(source.last_event_at).toLocaleString()}`
              : 'No events received yet'}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <button
            onClick={onConfigure}
            className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors duration-150 hover:border-ring/60 hover:text-foreground"
          >
            Configure
          </button>
          <button
            onClick={onOpenRules}
            className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors duration-150 hover:border-ring/60 hover:text-foreground"
          >
            Routing rules
          </button>
          <button
            onClick={onDelete}
            className={
              pendingDelete
                ? 'rounded-md border border-destructive bg-destructive/10 px-2 py-1 text-xs font-semibold text-destructive transition-colors duration-150'
                : 'rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors duration-150 hover:border-destructive/60 hover:text-destructive'
            }
            aria-live="polite"
          >
            {pendingDelete ? 'Confirm delete source' : 'Delete source'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SecretsRevealModal({ source, onClose }: { source: SourceCreated; onClose: () => void }) {
  // High-stakes once-shown-cant-be-seen UX: Continue is disabled until BOTH
  // secrets have been copied at least once. Prevents the "click Continue and
  // realize 10 seconds later I never copied the signing secret" lost-key
  // support ticket. Per audit P0 #11.
  const [copiedSk, setCopiedSk] = useState(false);
  const [copiedWh, setCopiedWh] = useState(false);
  const panelRef = useDialogFocus<HTMLDivElement>();
  const canContinue = copiedSk && copiedWh;
  const curlCmd = `curl -X POST https://api.apetech.studio/v1/events \\
  -H "Authorization: Bearer ${source.sk_live}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "external_id": "test-1",
    "type": "test",
    "title": "Hello from ${source.name}",
    "severity": "info"
  }'`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="secrets-reveal-title"
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 px-4"
    >
      <div ref={panelRef} className="w-full max-w-2xl space-y-5 rounded-2xl border border-border bg-card p-7 shadow-2xl">
        <div className="space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Save these now (shown once)
          </div>
          <h2 id="secrets-reveal-title" className="text-2xl font-semibold tracking-tight">"{source.name}" is live</h2>
          <p className="text-sm text-muted-foreground">
            Copy the API key + signing secret into your integration. Once you close this modal,
            only the prefix is recoverable; rotating regenerates the secret.
          </p>
        </div>
        <SecretField
          label="API key (Bearer)"
          value={source.sk_live}
          copied={copiedSk}
          onCopy={() => setCopiedSk(true)}
        />
        <SecretField
          label="Signing secret (X-APETech-Signature)"
          value={source.whsec}
          copied={copiedWh}
          onCopy={() => setCopiedWh(true)}
        />
        <div className="space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Quick test
          </div>
          <pre className="overflow-x-auto rounded-md border border-border bg-background p-3 text-[11px] leading-relaxed text-foreground">
{curlCmd}
          </pre>
        </div>
        <div aria-live="polite" className="text-xs text-muted-foreground">
          {canContinue
            ? '✓ Both secrets copied. Safe to continue.'
            : `Copy ${[!copiedSk && 'the API key', !copiedWh && 'the signing secret'].filter(Boolean).join(' and ')} to continue.`}
        </div>
        <button
          onClick={onClose}
          disabled={!canContinue}
          className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function SecretField({
  label,
  value,
  copied,
  onCopy,
}: {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
}) {
  async function copy() {
    try {
      await navigator.clipboard?.writeText(value);
      onCopy();
    } catch {
      /* clipboard blocked — user can select+Cmd+C from the visible code */
    }
  }
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {label}
        </div>
        {copied && (
          <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">
            ✓ copied
          </span>
        )}
      </div>
      <div className="flex gap-2">
        <code className="block flex-1 overflow-x-auto rounded-md border border-border bg-background px-3 py-2 font-mono text-[11px] text-foreground">
          {value}
        </code>
        <button
          onClick={copy}
          className={cn(
            'rounded-md border px-3 py-2 text-xs transition-colors',
            copied
              ? 'border-emerald-500/50 text-emerald-400'
              : 'border-border text-muted-foreground hover:border-ring/60 hover:text-foreground',
          )}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}
